import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { BLOG_POSTS, type BlogPost } from "@/lib/blog-posts";
import { getPosts, type Locale } from "@/lib/blog-posts-i18n";
import { ExternalLink, Copy, Check, Search } from "lucide-react";

const SITE = "https://kentro.com.br";

type Row = {
  key: string;
  locale: "pt" | "en" | "es";
  post: BlogPost;
  url: string;
  canonical: string;
  ogImage: string;
  twitterImage: string;
  category: string;
  author: string;
  publishedTime: string;
  modifiedTime: string;
  section: string;
  tag: string;
  title: string;
  description: string;
};

export const Route = createFileRoute("/blog-seo-preview")({
  head: () => ({
    meta: [
      { title: "Blog SEO Preview — Kentro (interno)" },
      { name: "description", content: "Página interna para validar metadados SEO/Open Graph dos posts." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BlogSeoPreviewPage,
});

function buildRows(): Row[] {
  const rows: Row[] = [];
  const build = (locale: "pt" | "en" | "es", post: BlogPost) => {
    const path = locale === "pt" ? `/blog/${post.slug}` : `/${locale}/blog/${post.slug}`;
    const url = `${SITE}${path}`;
    const image = post.cover ? `${SITE}${post.cover}` : `${SITE}/og-image.png`;
    rows.push({
      key: `${locale}:${post.slug}`,
      locale,
      post,
      url,
      canonical: url,
      ogImage: image,
      twitterImage: image,
      category: post.category,
      author: post.author,
      publishedTime: post.date,
      modifiedTime: post.date,
      section: post.category,
      tag: post.keyword,
      title: `${post.title} | Kentro`,
      description: post.description,
    });
  };
  BLOG_POSTS.forEach((p) => build("pt", p));
  (["en", "es"] as Locale[]).forEach((loc) => {
    getPosts(loc).forEach((p) => build(loc, p));
  });
  return rows;
}

function BlogSeoPreviewPage() {
  const rows = useMemo(buildRows, []);
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState<"all" | "pt" | "en" | "es">("all");

  const filtered = rows.filter((r) => {
    if (loc !== "all" && r.locale !== loc) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      r.post.title.toLowerCase().includes(s) ||
      r.post.slug.toLowerCase().includes(s) ||
      r.category.toLowerCase().includes(s)
    );
  });

  return (
    <div className="mx-auto max-w-6xl px-4 pt-24 pb-16 sm:px-6 lg:px-8">
      <header className="mb-8">
        <span className="inline-flex rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-yellow-300">
          Ferramenta interna · noindex
        </span>
        <h1 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl">
          Blog SEO Preview
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Validação de metadados por post: canonical, og:image, twitter:image, tags
          <code className="mx-1 rounded bg-white/10 px-1 py-0.5 text-xs">article:*</code> e teste rápido
          nos debuggers de Facebook, X/Twitter e LinkedIn.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por título, slug ou categoria…"
              className="w-full rounded-md border border-white/10 bg-white/5 py-2 pl-9 pr-3 text-sm outline-none focus:border-primary-glow/60"
            />
          </div>
          <div className="flex gap-1 rounded-md border border-white/10 bg-white/5 p-1 text-xs">
            {(["all", "pt", "en", "es"] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLoc(l)}
                className={`rounded px-2.5 py-1 font-semibold uppercase transition ${
                  loc === l ? "bg-primary-glow/30 text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <span className="ml-auto text-xs text-muted-foreground">
            {filtered.length} de {rows.length} entradas
          </span>
        </div>
      </header>

      <div className="space-y-6">
        {filtered.map((r) => (
          <PostRow key={r.key} row={r} />
        ))}
        {!filtered.length && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-8 text-center text-sm text-muted-foreground">
            Nenhum post encontrado com esses filtros.
          </div>
        )}
      </div>

      <div className="mt-10 text-center text-xs text-muted-foreground">
        <Link to="/blog" className="text-primary-glow hover:underline">← Voltar ao blog</Link>
      </div>
    </div>
  );
}

function PostRow({ row }: { row: Row }) {
  const debuggers = [
    { name: "Facebook", href: `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(row.url)}` },
    { name: "X/Twitter", href: `https://cards-dev.twitter.com/validator?url=${encodeURIComponent(row.url)}` },
    { name: "LinkedIn", href: `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(row.url)}` },
    { name: "Google Rich Results", href: `https://search.google.com/test/rich-results?url=${encodeURIComponent(row.url)}` },
  ];

  return (
    <article className="overflow-hidden rounded-2xl border border-white/10 bg-card/40">
      <div className="grid gap-0 md:grid-cols-[280px_1fr]">
        {/* OG Preview */}
        <div className="border-b border-white/10 bg-black/30 md:border-b-0 md:border-r">
          <div className="aspect-[1200/630] w-full overflow-hidden bg-neutral-900">
            <img
              src={row.ogImage}
              alt={row.post.coverAlt ?? row.post.title}
              loading="lazy"
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.opacity = "0.2";
              }}
            />
          </div>
          <div className="p-3 text-[11px] text-muted-foreground">
            <div className="truncate">{new URL(row.url).host}</div>
            <div className="mt-1 line-clamp-2 font-semibold text-foreground">{row.title}</div>
            <div className="mt-1 line-clamp-2">{row.description}</div>
          </div>
        </div>

        {/* Metadata */}
        <div className="p-5 md:p-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {row.locale}
            </span>
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-glow">
              {row.category}
            </span>
            <a
              href={row.url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1 text-xs text-primary-glow hover:underline"
            >
              Abrir post <ExternalLink className="size-3" />
            </a>
          </div>

          <h2 className="mt-2 text-lg font-bold">{row.post.title}</h2>
          <p className="mt-1 text-xs text-muted-foreground">/{row.post.slug}</p>

          <dl className="mt-4 space-y-2 text-xs">
            <MetaRow k="canonical" v={row.canonical} />
            <MetaRow k="og:url" v={row.url} />
            <MetaRow k="og:image" v={row.ogImage} />
            <MetaRow k="twitter:image" v={row.twitterImage} />
            <MetaRow k="article:author" v={row.author} />
            <MetaRow k="article:section" v={row.section} />
            <MetaRow k="article:tag" v={row.tag} />
            <MetaRow k="article:published_time" v={row.publishedTime} />
            <MetaRow k="article:modified_time" v={row.modifiedTime} />
          </dl>

          <div className="mt-5 flex flex-wrap gap-2">
            {debuggers.map((d) => (
              <a
                key={d.name}
                href={d.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold hover:bg-white/10"
              >
                Testar {d.name} <ExternalLink className="size-3" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

function MetaRow({ k, v }: { k: string; v: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(v);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* noop */
    }
  };
  return (
    <div className="grid grid-cols-[140px_1fr_auto] items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] px-2 py-1.5">
      <code className="truncate text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {k}
      </code>
      <code className="truncate text-foreground/90" title={v}>
        {v}
      </code>
      <button
        onClick={copy}
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-white/10 hover:text-foreground"
        title="Copiar"
      >
        {copied ? <Check className="size-3 text-primary-glow" /> : <Copy className="size-3" />}
      </button>
    </div>
  );
}
